import { IBaseAnalyticsCombined } from "@/store/analyticsDataStore";
import { useTheme } from "next-themes";
import { useEffect, useState } from "react";
import {
  filterByCurrencyAll,
  filterByDatesTimeSent,
} from "../analyticsHelperFunctions";
import { filterByDV01, insertSorted } from "./brokerRankHelperFunctions";
import { IBaseFilterBrokerRank } from "./BrokerRankIndex";
import BrokerRankMatrix from "./tables/BrokerRankMatrix";

export function normaliseNumInRange(
  value: number,
  min: number,
  max: number,
  biggerIsBetter = true,
) {
  if (max === min) return 1; // all values same
  return biggerIsBetter
    ? (value - min) / (max - min)
    : (max - value) / (max - min);
}

export function rankOptions(
  options: any[],
  dataFilters: IBaseFilterBrokerRank,
) {
  const multipliers = {
    TotalDV01: dataFilters.DVO1Percent,
    HitRate: dataFilters.HitRatePercent,
    Spread: dataFilters.SpreadPercent,
    TimeToQuote: dataFilters.TimePercent,
  };

  // Find min/max per category
  const categories = ["TotalDV01", "HitRate", "Spread", "TimeToQuote"];
  const stats = {} as any;
  categories.forEach((cat) => {
    stats[cat] = {
      min: Math.min(...options.map((o) => o[cat])),
      max: Math.max(...options.map((o) => o[cat])),
    };
  });

  return options
    .map((o) => {
      const DV01Total = normaliseNumInRange(
        o.TotalDV01,
        stats.TotalDV01.min,
        stats.TotalDV01.max,
        true,
      );
      // (o.TotalDV01 - stats.TotalDV01.min) /
      // (stats.TotalDV01.max - stats.TotalDV01.min);
      const HitRate = normaliseNumInRange(
        o.HitRate,
        stats.HitRate.min,
        stats.HitRate.max,
        true,
      );
      const Spread = normaliseNumInRange(
        o.Spread,
        stats.Spread.min,
        stats.Spread.max,
        false,
      );

      const TimeToQuote = normaliseNumInRange(
        o.TimeToQuote,
        stats.TimeToQuote.min,
        stats.TimeToQuote.max,
        false,
      );

      const score =
        DV01Total * multipliers.TotalDV01 +
        HitRate * multipliers.HitRate +
        Spread * multipliers.Spread +
        TimeToQuote * multipliers.TimeToQuote;

      return { ...o, score };
      // Normalize each category

      // Weighted score
    })
    .sort((a, b) => b.score - a.score); // highest first
}

function filterBrokerData(
  data: IBaseAnalyticsCombined[],
  dataFilters: IBaseFilterBrokerRank,
): IBaseAnalyticsCombined[] {
  //const structureFilteredData = filterByStructureAll(data, dataFilters); // Currently from Sankey -- NO LONGER USED as DV01 is the filter state.
  const filteredByDV01Data = filterByDV01(data, dataFilters);
  const dateFilteredData = filterByDatesTimeSent(
    filteredByDV01Data,
    dataFilters,
  );
  const currencyFiltered = filterByCurrencyAll(dateFilteredData, dataFilters);
  return currencyFiltered;
}

function faltternBrokerRankData(
  matrixData: any,
  dataFilters: IBaseFilterBrokerRank,
) {
  const childrenDataFlattened = [
    // , highestValue: 0 added for the colour code comparison.
    { field: "DV01Total" },
    { field: "HitRate" },
    { field: "Avg Spread (bps)" },
    { field: "TimeToQuote (min)" },
  ];

  const flattenedData = matrixData.map((matrixObject: any) => {
    const matrixWithRank = rankOptions(matrixObject.children, dataFilters);

    const childrenDataFlattenedDeepCopy = childrenDataFlattened.map(
      (child) => ({
        ...child,
        sortedArr: [],
      }),
    );
    const newObj = {
      ...matrixObject,
      children: childrenDataFlattenedDeepCopy,
      ranks: [],
    };

    matrixWithRank.forEach((e) => {
      newObj[e.Entity] = e.score;
    });

    const DV01Obj = newObj.children.find((cp: any) => cp.field == "DV01Total");
    if (DV01Obj) {
      matrixObject.children.forEach((child: any) => {
        DV01Obj[child.Entity] = Math.abs(Number(child.TotalDV01.toFixed(2)));
        insertSorted(DV01Obj.sortedArr, {
          name: child.Entity,
          value: Math.abs(Number(child.TotalDV01.toFixed(2))),
        });
      });
    }
    const hitRateObj = newObj.children.find((cp: any) => cp.field == "HitRate");
    if (hitRateObj) {
      matrixObject.children.forEach((child: any) => {
        hitRateObj[child.Entity] = Math.abs(Number(child.HitRate.toFixed(0)));
        insertSorted(hitRateObj.sortedArr, {
          name: child.Entity,
          value: Math.abs(Number(child.HitRate.toFixed(2))),
        });
      });
    }
    const spreadObj = newObj.children.find(
      (cp: any) => cp.field == "Avg Spread (bps)",
    );
    if (spreadObj) {
      matrixObject.children.forEach((child: any) => {
        spreadObj[child.Entity] = Math.abs(Number(child.Spread.toFixed(2)));
        insertSorted(spreadObj.sortedArr, {
          name: child.Entity,
          value: Math.abs(Number(child.Spread.toFixed(2))),
        });
      });
    }
    const timeToQuoteObj = newObj.children.find(
      (cp: any) => cp.field == "TimeToQuote (min)",
    );
    if (timeToQuoteObj) {
      matrixObject.children.forEach((child: any) => {
        timeToQuoteObj[child.Entity] = Math.abs(
          Number(child.TimeToQuote.toFixed(2)),
        );
        insertSorted(timeToQuoteObj.sortedArr, {
          name: child.Entity,
          value: Math.abs(Number(child.TimeToQuote.toFixed(2))),
        });
      });
    }
    const arrayOfValue = [];
    for (const key in newObj) {
      if (Object.prototype.hasOwnProperty.call(newObj, key)) {
        if (typeof newObj[key] === "number") {
          arrayOfValue.push(newObj[key]);
        }
      }
    }
    for (const key in newObj) {
      if (Object.prototype.hasOwnProperty.call(newObj, key)) {
        if (typeof newObj[key] === "number") {
          const rank = arrayOfValue.indexOf(newObj[key]);
          newObj.ranks.push({ [key]: rank + 1 });
        }
      }
    }
    return newObj;
  });
  return flattenedData;
}
type RankMap = { [key: string]: IBaseAnalyticsCombined[] };

function buildBrokerRankData(
  brokerTableData: IBaseAnalyticsCombined[],
  dataFilters: IBaseFilterBrokerRank,
) {
  const brokerDataReduced: any[] = brokerTableData.reduce(
    (acc: any, item: IBaseAnalyticsCombined) => {
      const instrumentType = item.instrumentType;
      if (!acc[instrumentType]) {
        acc[instrumentType] = [];
      }
      acc[instrumentType].push(item);
      return acc;
    },
    {},
  );

  // Start on the second layer.
  const layerTwoObject = {} as {
    [key: string]: RankMap[];
  };
  for (const key in brokerDataReduced) {
    if (brokerDataReduced.hasOwnProperty(key)) {
      layerTwoObject[key] = brokerDataReduced[key].reduce(
        (acc: any, item: any) => {
          const RPCustomer = item.RPCustomer;
          if (!acc[RPCustomer]) {
            acc[RPCustomer] = [];
          }
          acc[RPCustomer].push(item);
          return acc;
        },
        {},
      );
    }
  }

  const uniqueCounterparties: string | string[] = [];

  const matrixData = [];
  // Build the data for the rank table.
  for (const key in layerTwoObject) {
    const currentObj: Record<any, any> = {};
    currentObj.instrumentType = key;

    const instrumentTypeObj = {
      id: currentObj.instrumentType.replaceAll(" ", ""),
      InstrumentType: currentObj.instrumentType,
      children: [] as any,
    };
    for (const cp in layerTwoObject[key]) {
      //Add CPs to an array to keep track of how many exist
      if (!uniqueCounterparties.includes(cp)) {
        uniqueCounterparties.push(cp);
      }

      const cpAtStructureLevel: any = layerTwoObject[key][cp];

      let spreadTally = 0;
      let totalTimeToQuote = 0;

      const uniqueTradeIDs: string[] = [];
      const reducedCPData = cpAtStructureLevel.reduce(
        (acc: any, item: IBaseAnalyticsCombined) => {
          if (!acc[cp]) {
            acc["Entity"] = cp;
          }
          // if (!acc["InstrumentType"]) {
          //   acc["InstrumentType"] = currentObj.InstrumentType;
          // }

          const totalDV01 = "TotalDV01";
          // DV01 can be added per block as Total
          if (!acc[totalDV01]) {
            acc[totalDV01] = 0;
          }
          acc[totalDV01] = acc[totalDV01] + Number(item.DV01);
          // DV01 END

          if (!uniqueTradeIDs.includes(item.tradeID)) {
            uniqueTradeIDs.push(item.tradeID);

            // ONLY ADD AFFIRMED VALUE ONCE PER TRADE ID
            if (!acc["HitRateTally"]) {
              acc["HitRateTally"] = 0;
            }
            if (item.lastAction == "Affirmed") {
              acc["HitRateTally"] = acc["HitRateTally"] + 1;
            }

            if (!acc["UniqueTrades"]) {
              acc["UniqueTrades"] = 0;
            }
            acc["UniqueTrades"] = acc["UniqueTrades"] + 1;

            // TIME TO QUOTE ONLY ONCE PER TRADE TYPE:
            if (!acc["TimeToQuote"]) {
              acc["TimeToQuote"] = 0;
            }

            if (item.dateTimeQuoted && item.dateTimeSent && item.timeToQuote) {
              const timeInSeconds = Number(item.timeToQuote);

              //   new Date(item.dateTimeQuoted).getTime() -
              //     new Date(item.dateTimeSent).getTime(),
              // ) / 1000;
              totalTimeToQuote = totalTimeToQuote + timeInSeconds;
              acc["TimeToQuote"] = totalTimeToQuote / uniqueTradeIDs.length;
            }
          }

          // Calculate HITRATE
          if (!acc["HitRate"]) {
            acc["HitRate"] = (acc["HitRateTally"] / acc["UniqueTrades"]) * 100;
          }
          acc["HitRate"] = (acc["HitRateTally"] / acc["UniqueTrades"]) * 100;

          //SPREAD: Per Counterparty, for every block with a spread value - Ignore spreads of 0 and don't divide by them
          if (!acc["Spread"]) {
            acc["Spread"] = 0;
          }
          if (!acc["TotalSpread"]) {
            acc["TotalSpread"] = 0;
          }

          if (item.spreadBPS) {
            spreadTally++;
            acc["TotalSpread"] = acc["TotalSpread"] + Number(item.spreadBPS);
            acc["Spread"] = acc["TotalSpread"] / spreadTally;
          }
          return acc;
        },
        {},
      );
      instrumentTypeObj.children.push(reducedCPData);
    }
    matrixData.push(instrumentTypeObj);
  }
  const dataFlatterned = faltternBrokerRankData(matrixData, dataFilters);
  return { dataFlatterned, uniqueCounterparties };
}

const MainTableContainer = ({
  data,
  dataFilters,
}: {
  data: IBaseAnalyticsCombined[];
  dataFilters: IBaseFilterBrokerRank;
}) => {
  const { theme } = useTheme();

  const [brokerTableData, setBrokerTableData] = useState(
    filterBrokerData(data, dataFilters),
  );
  const { dataFlatterned, uniqueCounterparties } = buildBrokerRankData(
    brokerTableData,
    dataFilters,
  );

  // const [matrixData, setMatrixData] = useState(dataToReturn);
  const [allCPs, setAllCPs] = useState(uniqueCounterparties);

  useEffect(() => {
    const newBrokerData = filterBrokerData(data, dataFilters);
    setBrokerTableData(newBrokerData);
    const { uniqueCounterparties } = buildBrokerRankData(
      newBrokerData,
      dataFilters,
    );
    // setMatrixData(dataToReturn);
    setAllCPs(uniqueCounterparties);
  }, [data, dataFilters]);

  return (
    <div className={` flex h-full w-full flex-col gap-1 p-1`}>
      <div
        className={`${theme == "dark" ? "agChartCubeBorderDark" : "agChartCubeBorder"} border-thin flex h-full w-full flex-col p-1`}
      >
        <div className="flex h-full w-full flex-col">
          {dataFilters.range.length > 0 ? (
            <BrokerRankMatrix
              dataFlatterned={
                dataFilters.range.length > 0 ? dataFlatterned : []
              }
              allCPs={allCPs}
              dataFilters={dataFilters}
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center ">
              <div className="border bg-slate-300 p-1 px-2 dark:bg-slate-800">
                loading ...
              </div>
            </div>
          )}
        </div>
        {/* TEMP OFF */}
      </div>
      {/* <div
        className={`${theme == "dark" ? "agChartCubeBorderDark" : "agChartCubeBorder"} border-thin flex h-2/3 w-full flex-col p-1`}
      >
        <div className="flex h-full w-full flex-col">
          <BrokerStatistics
            dataFlatterned={dataFilters.range.length > 0 ? dataFlatterned : []}
            dataFilters={dataFilters}
            allCPs={allCPs}
          />
        </div>
      </div> */}
    </div>
  );
};

export default MainTableContainer;
